/**
 * domains/ai/thesis2/useStructuredThesis.ts — MER Algo Footprints v7.1H
 *
 * React hook that:
 *   1. Reads the current StructuredThesis for a symbol from the store
 *   2. Provides a trigger to run the full pipeline
 *   3. Exposes loading + error state
 *
 * The hook injects an AICaller that routes through the existing
 * Anthropic API path used by the rest of the app.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useStore }                   from '../../../shared/store';
import { getSymbolContext }           from '../../market/selectors/symbolContext';
import { getThesis2, applyThesis2, subscribe2, SymbolThesis2 } from './store';
import { buildThesisEvidence }        from './evidence';
import { generateStructuredThesis, AICaller } from './pipeline';
import { StructuredThesis, GateConfig, DEFAULT_GATE_CONFIG } from './types';


// ─── Anthropic API caller (mirrors existing ThesisPanel approach) ─────────────

const MODEL_ID = 'claude-sonnet-4-6';

function makeAICaller(apiKey: string): AICaller {
  return async (
    system:   string,
    messages: Array<{role: 'user' | 'assistant'; content: string}>,
  ): Promise<{ text: string; modelId: string }> => {
    const resp = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'Content-Type':      'application/json',
        'x-api-key':         apiKey,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model:      MODEL_ID,
        max_tokens: 4096,
        system,           // separate system prompt — enables prompt caching
        messages,
      }),
    });
    if (!resp.ok) throw new Error(`Anthropic API ${resp.status}: ${await resp.text()}`);
    const data = await resp.json();
    const block = data.content?.find((b: any) => b.type === 'text');
    if (!block?.text) throw new Error('No text block in Anthropic response');
    const actualModelId = data.model ?? MODEL_ID;
    return { text: block.text, modelId: actualModelId };
  };
}

// ─── Hook result ──────────────────────────────────────────────────────────────

export interface UseStructuredThesisResult {
  symbolThesis:  SymbolThesis2;
  current:       StructuredThesis | null;
  loading:       boolean;
  error:         string | null;
  runAnalysis:   () => Promise<void>;
  isStale:       boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useStructuredThesis(
  symbol:     string,
  gateConfig: GateConfig = DEFAULT_GATE_CONFIG,
): UseStructuredThesisResult {
  const { symbolData, settings, weights } = useStore();

  const [symbolThesis, setSymbolThesis] = useState<SymbolThesis2>(() => getThesis2(symbol));
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const abortRef = useRef(false);

  // Subscribe to store changes
  useEffect(() => {
    setSymbolThesis(getThesis2(symbol));
    const unsub = subscribe2(() => setSymbolThesis(getThesis2(symbol)));
    return unsub;
  }, [symbol]);

  const runAnalysis = useCallback(async () => {
    const apiKey = settings.anthropicApiKey;
    if (!apiKey) {
      setError('No API key configured — check Settings');
      return;
    }

    const data = symbolData[symbol];
    const ctx  = data?.candles?.length
      ? getSymbolContext(symbol, data, settings, weights)
      : null;

    if (!ctx) {
      setError('No market data for ' + symbol);
      return;
    }

    setLoading(true);
    setError(null);
    abortRef.current = false;

    try {
      const prior   = getThesis2(symbol).current;
      const input   = buildThesisEvidence(ctx, settings, null, prior, 0);
      const caller  = makeAICaller(apiKey);
      const result  = await generateStructuredThesis(input, caller, gateConfig);

      if (!abortRef.current) {
        applyThesis2(result.thesis);
      }
    } catch (err: any) {
      if (!abortRef.current) {
        setError(err?.message ?? 'Unknown error during thesis generation');
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  }, [symbol, symbolData, settings, weights, gateConfig]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { abortRef.current = true; };
  }, []);

  return {
    symbolThesis,
    current:   symbolThesis.current,
    loading,
    error,
    runAnalysis,
    isStale:   symbolThesis.isStale,
  };
}
